'use client';

import { X } from 'lucide-react';
import * as React from 'react';

import { useControl } from 'src/hooks';
import { cn } from 'src/lib/utils';

import { Input as BaseInput } from '../ui/input';

export interface InputProps extends Omit<React.ComponentProps<'input'>, 'prefix'> {
  // 前缀元素（显示在输入框内左侧）
  prefix?: React.ReactNode;
  // 后缀元素（显示在输入框内右侧）
  suffix?: React.ReactNode;
  // 是否可清除（显示清除按钮）
  clearable?: boolean;
  // 清除时的回调
  onClear?: () => void;
  // 回车键回调
  onEnter?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  // 值变化回调
  onValueChange?: (value: string) => void;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      prefix,
      suffix,
      clearable = false,
      onClear,
      onEnter,
      onValueChange,
      onChange,
      onKeyDown,
      value,
      defaultValue = '',
      ...props
    },
    ref
  ) => {
    const [currentValue, setValue] = useControl(defaultValue, value, (newValue) => {
      // 调用 onValueChange 回调
      onValueChange?.(String(newValue));

      // 触发原生 onChange 事件
      if (onChange) {
        const syntheticEvent = {
          target: { value: String(newValue) },
          currentTarget: { value: String(newValue) },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    });

    // 是否显示清除按钮
    const showClearButton = clearable && currentValue && String(currentValue).length > 0;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setValue(newValue);
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      setValue('');
      onClear?.();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        onEnter?.(event);
      }
      onKeyDown?.(event);
    };

    // 分离布局类和输入框样式类
    const layoutClasses = [
      'hidden',
      'block',
      'inline',
      'inline-block',
      'flex',
      'inline-flex',
      'grid',
      'inline-grid',
      'contents',
    ];
    const breakpointPrefixes = ['sm:', 'md:', 'lg:', 'xl:', '2xl:'];

    const isLayoutClass = (cls: string) => {
      // 检查是否是布局类
      if (layoutClasses.some((layoutCls) => cls === layoutCls)) return true;
      // 检查是否是响应式布局类 (如 lg:flex, md:hidden 等)
      return breakpointPrefixes.some(
        (prefix) =>
          cls.startsWith(prefix) &&
          layoutClasses.some((layoutCls) => cls === `${prefix}${layoutCls}`)
      );
    };

    const classNames = className?.split(' ') || [];
    const layoutClassNames = classNames.filter(isLayoutClass);
    const inputClassNames = classNames.filter((cls) => !isLayoutClass(cls));

    return (
      <div className={cn('relative', ...layoutClassNames)}>
        {/* 前缀元素 */}
        {prefix && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
            {prefix}
          </div>
        )}

        {/* 输入框 */}
        <BaseInput
          className={cn({ 'pr-9': clearable }, ...inputClassNames)}
          ref={ref}
          value={currentValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          {...props}
        />

        {/* 后缀区域 */}
        {(showClearButton || suffix) && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
            {/* 清除按钮 */}
            {showClearButton && (
              <span
                role="button"
                tabIndex={-1}
                onClick={handleClear}
                className="h-6 w-6 flex items-center justify-center text-muted-foreground hover:text-accent-foreground -mr-1 select-none outline-none rounded-md transition hover:shadow-xs hover:bg-accent"
                aria-label="Clear input"
              >
                <X className="h-4 w-4" />
              </span>
            )}

            {/* 分隔线 - 只有在同时显示清除按钮和后缀时才显示 */}
            {showClearButton && suffix && <div className="ml-2 mr-1 h-4 w-px bg-border" />}

            {/* 后缀元素 */}
            {suffix && <div className="flex items-center justify-center min-w-0">{suffix}</div>}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
